/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Safely access Vite environment variables
const getEnv = (key: string) => {
  try {
    // @ts-ignore
    return (import.meta && import.meta.env) ? import.meta.env[key] : '';
  } catch (e) {
    return '';
  }
};

export const PINATA_JWT = getEnv('VITE_PINATA_JWT');
export const GATEWAY_URL = "https://gateway.pinata.cloud/ipfs/";

/**
 * Standard Token Metadata interface for IPFS storage.
 */
export interface TokenMetadata {
  id: string;
  name: string;
  symbol: string;
  description: string;
  image: string;
  header_image?: string;
  external_url?: string;
  tags?: string[];
  properties?: {
    chain?: 'SOL' | 'SUI';
    network?: 'mainnet' | 'devnet' | 'testnet';
    creator?: string;
    mintAddress?: string;
    marketId?: string;
    txHash?: string;
    createdAt?: number;
    tokenStandard?: 'SPL' | 'TOKEN_2022' | 'SUI_COIN';
    isLive?: boolean;
    aiAnalyticsEnabled?: boolean;
    extensions?: {
        transferFeeBasisPoints?: number;
        maxFee?: number;
        interestRate?: number;
        isImmutable?: boolean;
        isMintable?: boolean;
    };
    twitter?: string;
    telegram?: string;
    discord?: string;
    website?: string;
    [key: string]: any;
  };
}

export const uploadFileToPinata = async (file: File): Promise<string> => {
    if (!PINATA_JWT) {
        console.warn("Pinata JWT missing. Simulating upload.");
        // Return a local object URL for preview if no API key is present
        return URL.createObjectURL(file);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    const metadata = JSON.stringify({ name: `image-${Date.now()}` });
    formData.append('pinataMetadata', metadata);
    
    const options = JSON.stringify({ cidVersion: 1 });
    formData.append('pinataOptions', options);

    try {
        const res = await fetch("https://api.pinata.cloud/pinning/pinFileToIPFS", {
            method: "POST",
            headers: { Authorization: `Bearer ${PINATA_JWT}` },
            body: formData,
        });
        
        if (!res.ok) throw new Error("Failed to upload image to Pinata");
        const data = await res.json();
        return `${GATEWAY_URL}${data.IpfsHash}`;
    } catch (e) {
        console.error("Pinata Upload Error:", e);
        return URL.createObjectURL(file);
    }
}

export const uploadMetadataToPinata = async (metadata: TokenMetadata): Promise<string> => {
    if (!PINATA_JWT) {
        console.warn("Pinata JWT missing. Simulating metadata upload.");
        return "https://gateway.pinata.cloud/ipfs/QmSimulatedMetadataHash456";
    }

    const payload = {
        pinataOptions: { cidVersion: 1 },
        pinataMetadata: { name: `${metadata.symbol}-metadata-${Date.now()}` },
        pinataContent: metadata
    };

    try {
        const res = await fetch("https://api.pinata.cloud/pinning/pinJSONToIPFS", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${PINATA_JWT}`,
            },
            body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error("Failed to upload metadata to Pinata");
        const data = await res.json();
        return `${GATEWAY_URL}${data.IpfsHash}`;
    } catch (e) {
        console.error("Pinata Metadata Upload Error:", e);
        throw e;
    }
}

const LOCAL_STORAGE_KEY = 'hot_local_tokens_v1';

export const saveLocalToken = (token: TokenMetadata) => {
    try {
        if (!token.id) {
            token.id = token.properties?.mintAddress || `local-${Date.now()}`;
        }

        const existing = localStorage.getItem(LOCAL_STORAGE_KEY);
        let tokens: TokenMetadata[] = existing ? JSON.parse(existing) : [];
        tokens = [token, ...tokens];
        
        // Remove duplicates based on mint address
        const seen = new Set();
        const uniqueTokens = tokens.filter(t => {
            const id = t.properties?.mintAddress;
            if (id && seen.has(id)) return false;
            if (id) seen.add(id);
            return true;
        });

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(uniqueTokens));
        
        // Dispatch event so other components (Market, AI) update immediately
        window.dispatchEvent(new Event('hot_tokens_updated'));
    } catch (e) {
        console.error("Failed to save local token", e);
    }
}

export const getLocalTokens = (): TokenMetadata[] => {
    try {
        const existing = localStorage.getItem(LOCAL_STORAGE_KEY);
        return existing ? JSON.parse(existing) : [];
    } catch (e) {
        return [];
    }
}

export const clearLocalTokens = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    window.dispatchEvent(new Event('hot_tokens_updated'));
}

export const fetchRecentMetadata = async (pageOffset: number = 0, pageLimit: number = 12, isMainnet: boolean = false): Promise<{ tokens: TokenMetadata[], hasMore: boolean }> => {
   // 1. Get Local Tokens (User generated)
   const localTokens = getLocalTokens();
   
   // Filter local tokens by network to respect the environment
   const filteredLocal = localTokens.filter(t => {
       const tokenNetwork = t.properties?.network || 'devnet';
       return isMainnet ? tokenNetwork === 'mainnet' : tokenNetwork !== 'mainnet';
   });
   
   // 2. Generate Mocks only if NOT Mainnet (Devnet simulation)
   let fetchedTokens: TokenMetadata[] = [];
   
   if (!isMainnet) {
       const mockCount = 60;
       fetchedTokens = Array.from({ length: mockCount }).map((_, i) => {
           const numericId = i + 1;
           const isSui = numericId % 3 === 0;
           const isTax = numericId % 4 === 0;
           const assetId = `${isSui ? 'sui' : 'sol'}-${numericId}`;

           // More realistic ID generators
            const generateBase58 = (length: number) => {
                const chars = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
                let result = '';
                for (let i = 0; i < length; i++) {
                    result += chars.charAt(Math.floor(Math.random() * chars.length));
                }
                return result;
            };
            const generateHex = (length: number) => {
                const chars = "0123456789abcdef";
                let result = '';
                for (let i = 0; i < length; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
                return result;
            };

            const txHash = isSui ? generateBase58(44) : generateBase58(87);
            const creatorAddress = isSui ? `0x${generateHex(64)}` : generateBase58(44);
            const mintAddress = isSui ? `0x${generateHex(62)}::gem::GEM` : generateBase58(44);
           
           return { 
               id: assetId,
               name: isSui ? `Sui Gem #${numericId}` : `Solana Asset #${numericId}`, 
               symbol: isSui ? `GEM${numericId}` : `SOLA${numericId}`, 
               description: `A simulated token project #${numericId} launched on HotLaunch Devnet.`, 
               image: `https://placehold.co/400x400/${isSui ? '2a9d8f' : '1a1a1a'}/ffffff?text=${numericId}`,
               header_image: `https://placehold.co/1200x400/${isSui ? '2a9d8f' : '1a1a1a'}/ffffff?text=Project+${numericId}`,
               tags: isSui ? ['Sui', 'Meme'] : ['Solana', 'DeFi'],
               properties: { 
                   chain: isSui ? 'SUI' : 'SOL', 
                   network: 'devnet',
                   txHash: txHash,
                   creator: creatorAddress,
                   createdAt: Date.now() - (numericId * 3600000) - 100000,
                   mintAddress: mintAddress,
                   tokenStandard: isSui ? 'SUI_COIN' : (isTax ? 'TOKEN_2022' : 'SPL'),
                   isLive: false,
                   aiAnalyticsEnabled: true,
                   extensions: isTax ? {
                       transferFeeBasisPoints: 100, // 1%
                       maxFee: 5000,
                       isImmutable: true,
                       isMintable: false
                   } : undefined
               }
           };
       });
   }

   // Combine Local + Fetched
   // On Mainnet, this will simply be the user's local tokens (clean list)
   // On Devnet, this will be User Local Tokens + Generated Demos
   const allTokens = [...filteredLocal, ...fetchedTokens];
   
   // Sort by newest first
   allTokens.sort((a, b) => (b.properties?.createdAt || 0) - (a.properties?.createdAt || 0));

   const sliced = allTokens.slice(pageOffset, pageOffset + pageLimit);
   
   return {
       tokens: sliced,
       hasMore: (pageOffset + pageLimit) < allTokens.length
   };
}